import { GoogleMap, HeatmapLayer, Marker, useLoadScript } from "@react-google-maps/api";
import { useMemo } from "react";

import type { FleetUnit, HeatmapResponse } from "@/api/fleet";

const center = { lat: 19.4326, lng: -99.1332 };

const colorForEstado: Record<string, string> = {
  patrullando: "#10B981",
  en_operativo: "#8B5CF6",
  hit_activo: "#BE123C",
  detenido: "#F59E0B",
  en_pausa: "#F59E0B",
  offline: "#6B7280",
};

function svgMarkerIcon(color: string): google.maps.Symbol {
  return {
    path: 0, // CIRCLE
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#0F1117",
    strokeWeight: 2,
    scale: 7,
  } as google.maps.Symbol;
}

const DARK_MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#4b6878" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#304a7d" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
];

interface Props {
  units: FleetUnit[];
  heatmap: HeatmapResponse | null;
  layers: { markers: boolean; heatmap: boolean };
  onSelectUnit: (unit: FleetUnit) => void;
}

export function FleetMap({ units, heatmap, layers, onSelectUnit }: Props) {
  const apiKey = (import.meta as ImportMeta).env?.VITE_GOOGLE_MAPS_KEY ?? "";
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: ["visualization"],
  });

  const markers = useMemo(
    () =>
      units.filter(
        (u): u is FleetUnit & { latitude: number; longitude: number } =>
          u.latitude !== null && u.longitude !== null
      ),
    [units]
  );

  const heatmapData = useMemo(() => {
    if (!heatmap || !window.google) return [];
    return heatmap.cells.map((c) => ({
      location: new google.maps.LatLng(c.lat, c.lng),
      weight: c.count,
    }));
  }, [heatmap]);

  if (!apiKey) {
    return (
      <div className="h-full flex items-center justify-center text-muted-200">
        Configura VITE_GOOGLE_MAPS_KEY
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className="h-full flex items-center justify-center text-muted-200">
        Cargando mapa…
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: "100%" }}
      center={center}
      zoom={6}
      options={{
        styles: DARK_MAP_STYLE,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      }}
    >
      {layers.heatmap && heatmap && (
        <HeatmapLayer
          data={heatmapData}
          options={{ radius: 24, opacity: 0.6 }}
        />
      )}
      {layers.markers &&
        markers.map((u) => (
          <Marker
            key={u.unitId}
            position={{ lat: u.latitude, lng: u.longitude }}
            icon={svgMarkerIcon(colorForEstado[u.estado] ?? "#6B7280")}
            title={u.alias}
            onClick={() => onSelectUnit(u)}
          />
        ))}
    </GoogleMap>
  );
}
